import React from 'react';
import BetterWMS from './betterWMS';
import {mapLayers, baseMaps, polyIcon, moveIcon, resizeIcon} from './leafletConfig';

/**
 * VBLeaflet — shared Leaflet map wrapper for vb-example-component.
 *
 * ## Integration requirements (read before using)
 *
 * ### 1. Light DOM only — never inside TailwindWrapper
 *
 * `componentDidMount` calls `L.map(this.props.id)`, which uses `document.getElementById`.
 * That API cannot see elements rendered inside a shadow root.
 *
 * `TailwindWrapper` (`src/utils/TailwindWrapper.jsx`) portals children into an open
 * shadow root for scoped Tailwind CSS. **Do not render `<VBLeaflet>` as a descendant
 * of `<TailwindWrapper>`.** If the page uses TailwindWrapper for other UI, split the
 * tree so the map sits in the light DOM between wrapper blocks (or use inline styles
 * for the map chrome).
 *
 * Failure symptoms:
 * - `Map container not found` (container id not found in document)
 * - `L is not defined` (Leaflet scripts not loaded — see below)
 *
 * Working patterns: `Karta.js`, `SellTimberMap.js`, `SkrytPage.js`, `Auctions.js`.
 *
 * When mixing with TailwindWrapper on the same page, render VBLeaflet in a light-DOM
 * slot (e.g. ReactDOM.createPortal) between TailwindWrapper sections. See `SkrytPage.js`.
 *
 * ### 2. Load Leaflet from the Django template
 *
 * The global `L` object is not bundled with this component. Pages that mount VBLeaflet
 * must include Leaflet assets in their template meta block:
 *
 *   {% include 'blobs/leaflet.html' %}
 *
 * See e.g. `auctions.html`, `min-skog.html`, `skryt.html`.
 *
 * ### 3. Container sizing
 *
 * The rendered `<div id={props.id}>` uses `height: 100%` / `width: 100%`. An ancestor
 * must have an explicit height (e.g. `style={{ height: '420px' }}` or a fixed Tailwind
 * height class on a light-DOM parent).
 */

/** Leaflet.draw reads `L.drawLocal` for cursor tooltips (default English). */
function ensureSwedishLeafletDrawLocale() {
    if (typeof L === 'undefined' || !L.drawLocal?.draw?.handlers) return;
    try {
        const poly = L.drawLocal.draw.handlers.polygon;
        if (poly?.tooltip) {
            poly.tooltip.start = 'Klicka för att börja rita området.';
            poly.tooltip.cont = 'Klicka för att fortsätta rita området.';
            poly.tooltip.end = 'Klicka på första punkten för att stänga området.';
        }
        const line = L.drawLocal.draw.handlers.polyline;
        if (line?.tooltip) {
            line.tooltip.start = 'Klicka för att börja rita linjen.';
            line.tooltip.cont = 'Klicka för att fortsätta rita linjen.';
            line.tooltip.end = 'Klicka på sista punkten för att avsluta linjen.';
        }
    } catch (e) {
        /* ignore if leaflet.draw not loaded */
    }
}

class VBLeaflet extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            control: null,
            deletedLayers: L.featureGroup([]),
            editingLayers: L.featureGroup([]),
            mapLayers: L.layerGroup([]),
            baseMap: L.layerGroup([]),
            mode: 'view'
        }
    }

    /*
        VB-Leaflet map component

        DOM / PAGE SETUP (see file-level JSDoc above)
        - Must render in the light DOM, not inside TailwindWrapper (shadow root).
        - Parent Django template must {% include 'blobs/leaflet.html' %}.
        - Give the map container a sized ancestor; this component fills its parent.

        STATES
        - This component is using a state called 'mode' which indicates what you are doing on the map e.g. editing, deleting layers.
        - deletedLayers will add layers to a featureGroup that you are currently deleting in order to revert the changes if the user chooses to cancel.
        - editingLayers will add layers to a featureGroup in order to revert changes should the user choose to cancel changes.
        - control is used for the drawing functionality. You could technically interact with this state after you have initiated drawing.
        - mapLayers will store all the tileLayers that show specific data. In case they are being activated they will be added to this state in case you want to interact with them.

        FUNCTIONALITIES
        - When you create new 'drawn' layers always add 'type' to the options of the layer in order to activate all the functionality.
        - Whenever you add new 'drawn' layers (e.g. polygons, markers..) to the map makes sure to run the setLayer() function on the layer or setLayers() if multiple. For drawing this functionality is automated.
        - When creating layers make sure to set any properties you want on the layers before adding them to the map. layer.on('mouseover') or layer.on('mouseout') are examples of layer actions.
        - In Leaflet if you place layers directly instead of using the leaflet in-build geojson function you need to swap coordinates. Use the function from leafletConfig.js called swapCoords().
        - Whenever we add more background maps or data layers into leaflet add them in the leafletConfig file and give them a appropriate name. This way we will always have all layers available where ever we need them.

        EXAMPLES
        - Here is an example of options you send in as second argument when placing a new marker in the parent:
            icon: L.icon({
                iconUrl: `${Window.static_path}img/map-markers/marker-onfire.svg`,
                iconSize: [38, 95],
                iconAnchor: [18, 74]
            })
        - Setting type on a layer (polygon, will work for all sort of layers):
            L.polygon(coordinates), {type: 'polygon'})
        - If you want to disable editing/clicking on a layer send { interactive: false } to the options/settings of the layer:
            L.polygon(coordinates), {type: 'polygon', interactive: false})
    */

    startDraw = (type, options) => {
        ensureSwedishLeafletDrawLocale();
        // Start to draw any of the following shapes
        // options: Polyline, Polygon, Rectangle, Circle, Marker, CircleMarker
        let baseOptions = {
            icon: polyIcon, 
            shapeOptions: {
                color: '#d9cec1',
                weight: 3,
                fillColor: '#dfff5d',
                fillOpacity: .2
            }
        }

        const control = new L.Draw[type](this.state.map, {...baseOptions, ...options})
        control.enable()
        this.setState({ control: control })
    }

    completeDraw = () => {
        // Used when you are drawing a new shape and want to complete it with an external button
        this.state.control.completeShape()
        this.setState({control: null})
    }

    stopDraw = () => {
        // Cancel drawing on the map
        this.state.control.disable()
        this.setState({control: null})
    }

    finishDraw(e) {
        // When you are drawing a new layer on the map trigger this function when you are done.
        // options: polyline, polygon, rectangle, circle, marker
        if (this.props.finishDraw) {
            this.props.finishDraw(e);
        }

        e.layer.options.type = e.layerType
        this.setLayer(e)
        this.state.map.addLayer(e.layer);
    }

    clearMap() {
        // Clear map from any drawn layers e.g. polygons, markers...
        this.state.map.eachLayer(l => {
            if(l.options.drawn) {
                this.state.map.removeLayer(l);
            }
        })
    }

    revert() {
        // When editing use this function to cancel anything you have been editing and revert to before enabling editing mode.
        this.state.deletedLayers.addTo(this.state.map)
        this.state.editingLayers.eachLayer(l => {
            l.fire('revert')
        })

        this.setMode('view')
        this.setState({ deletedLayers: L.featureGroup([]), editingLayers: L.featureGroup([]) })
    }

    save() {
        // When editing use this function to confirm the changes on the map and close editing.
        this.state.editingLayers.eachLayer(l => {
            l.fire('save')
        })

        this.setMode('view')
        this.setState({ deletedLayers: L.featureGroup([]), editingLayers: L.featureGroup([]) })
    }

    setMode(mode) {
        // Sets mode of the map, options are either 'edit' or 'delete'. Anything else will default to 'view' mode
        if((mode == 'edit' && this.state.mode == 'delete') || (mode == 'delete' && this.state.mode == 'edit') ) {
            this.revert()
        }
        this.setState({ mode: mode })
    }

    zoomIn() {
        this.setZoom(this.state.map.getZoom() + 1)
    }

    zoomOut() {
        this.setZoom(this.state.map.getZoom() - 1)
    }

    setZoom(zoomLevel) {
        this.state.map.setZoom(zoomLevel);
    }

    handleZoom = (e) => {
        if(this.props.handleZoom) {
            this.props.handleZoom(e)
        }
    }

    setBaseMap(map) {
        const layerType = typeof map

        if(!map){
            if(this.state.baseMap) {
                this.state.map.removeLayer(this.state.baseMap)
                return;
            }
        }

        // Change to another baseMap based on if name or obj
        let baseMapObj = map;
        if (layerType == "string"){
            baseMapObj = baseMaps.find(m => m.name == map)
        } else if(!baseMapObj.hasOwnProperty("config")){
            baseMapObj.config = {
                minZoom: 13, 
                maxZoom: 19,
                layers: map.layer, 
                tileSize: baseMapObj.name.startsWith("LM Orto") ? 256 : 512, 
                transparent: true, 
                tiled: true, 
                f: 'pjson', 
                format: 'image/jpeg', 
                identify: false, 
                zIndex: 1000
            }
        }

        if(baseMapObj) {
            if(this.state.baseMap) {
                this.state.map.removeLayer(this.state.baseMap)
            }

            let baseMap = null
            if(baseMapObj?.type == "wms") {
                baseMap = L.tileLayer.wms(baseMapObj.url, baseMapObj.config)
            } else {
                baseMap = L.tileLayer(baseMapObj.url, baseMapObj.config)
            }
            baseMap.name = baseMapObj.name

            /* This made the maxZoom from leafletConfig not work: 
            let maxZoom = 18
            this.state.mapLayers.eachLayer(l => {
                if(l.options.maxZoom > maxZoom) {
                    maxZoom = l.options.maxZoom
                }
            })
            baseMap.options.maxZoom = maxZoom */

            baseMap.setZIndex(1)

            baseMap.addTo(this.state.map)
            this.setState({baseMap: baseMap})
        }
    }

    betterWMSPopup(e, extraData, content) {
        let data = extraData(content)

        let jsx = ''
        // Each item in this data can be anything since the WMS layers provide different kinds of data. It is just a standardized way of looping over it, whatever it is. That is why I used the 'i' for 'item'. Very generic, yes. :) 
        Object.entries(data).forEach(i => {
            if(i[1] != "Null"){
                jsx += `<span class="font-bold caption">${i[0]}:</span><br />`
            
                if(i[1].indexOf("https://") != -1 || i[1].indexOf("http://") != -1){
                    jsx += `<span class="mb-1"><a class="break-words" target="_blank" href=${i[1]}>${i[1]}</a></span><br />`
                } else {
                    jsx += `<span class="mb-1">${i[1]}</span><br />`
                } 
            }
        })

        if(jsx.length > 0){
            L.popup({ maxWidth: 800}).setLatLng([e.latlng.lat, e.latlng.lng]).setContent(jsx).openOn(this.state.map);
        }
        
    }

    toggleMapLayer(layerObj) {
        // Toggle a certain map layer on/off depending on the given 'name' property from leafletConfig.js
        const layerType = typeof layerObj

        let layerExist = null
        let layers = this.state.mapLayers
        layers.eachLayer(l => {
            if(layerType == 'string') {
                if(l.options.name == layerObj) {
                    layerExist = l
                }
            } else {
                if(l.options.layers == layerObj.layer) {
                    layerExist = l
                }
            }
        })

        if(layerExist) {
            layers.removeLayer(layerExist)
            this.state.map.removeLayer(layerExist)
            if(this.state.map.getZoom() > 18) {
                this.setZoom(18)
            }
        } else {
            if(layerType == 'string') {
                layerObj = mapLayers.find(l => l.name == layerObj)
            }
            
            const maxZoom = layerObj?.options?.maxZoom
            if(maxZoom) {
                this.state.baseMap.options.maxZoom = maxZoom
            }

            let mapLayer = BetterWMS(layerObj.url, {
                name: layerObj.name,
                layers: layerObj.layer,
                transparent: true,
                tiled: true,
                f: 'pjson',
                format: 'image/png',
                identify: false,
                ...layerObj?.options
            })
            
            if(layerObj.extraData) {
                mapLayer.getData = (e, content) => this.betterWMSPopup(e, layerObj.extraData, content)
            }

            if(layerType == 'string') {
                mapLayer.name = layerObj.name
            } else {
                mapLayer.name = layerObj.layer
            }
            
            
            layers.addLayer(mapLayer)
            layerObj.style == mapLayer.setZIndex(2)
            
            this.state.map.addLayer(mapLayer)
        }

        this.setState({ mapLayers: layers })
    }

    handleMapClick(e) {
        if(this.props.handleMapClick) {
            this.props.handleMapClick(e)
        }
    }

    setLayers(layers) {
        // Easy function to set functionality on a FeatureGroup
        layers.eachLayer(e => {
            this.setLayer(e)
        })
    }

    setLayer(e) {
        // setLayer sets all functionality needed to interact with a layer.
        let layer = e.layer ? e.layer : e
        let latlngsLayers = ['polyline', 'polygon', 'rectangle']
        let latlngLayers = ['marker', 'circle', 'circlemarker']
        
        if(layer.options.type != 'marker') {
            let options = layer.options
            let baseOptions = {
                drawn: true,
                color: '#d9cec1',
                weight: 3,
                fillColor: '#dfff5d',
                fillOpacity: .2
            }
            layer.setStyle({...baseOptions, ...options})
        } else {
            layer.options.drawn = true
        }
        
        if(layer.options.type == 'marker') {
            layer.off('click')
        }

        if(layer.options.type == 'circle') {
            layer.originalRadius = JSON.parse(JSON.stringify(layer.getRadius()))
        }

        if(latlngsLayers.includes(layer.options.type)) {
            layer.originalCoords = JSON.parse(JSON.stringify(layer.getLatLngs()))
        }

        if(latlngLayers.includes(layer.options.type)) {
            layer.originalCoords = JSON.parse(JSON.stringify(layer.getLatLng()))
        }

        layer.on('click', () => {
            if(this.state.mode == 'edit') {
                if(layer.editing) {
                    let layers = this.state.editingLayers
                    layer.addTo(layers)
                    layer.editing.enable()
                    
                    this.setState({ editingLayers: layers })
                }
            } else if(this.state.mode == 'delete') {
                if(layer.editing) {
                    let layers = this.state.deletedLayers
                    layer.addTo(layers)
                    this.setState({ deletedLayers: layers })
                    this.state.map.removeLayer(layer)
                }
            } else {
                if(layer.options.type == 'marker') {
                    layer.togglePopup()
                }
            }
        })

        layer.on('save', () => {
            if(layer.editing) {
                layer.editing.disable()
            }

            if(layer.options.type == 'circle') {
                layer.originalRadius = JSON.parse(JSON.stringify(layer.getRadius()))
            }

            if(latlngsLayers.includes(layer.options.type)) {
                layer.originalCoords = JSON.parse(JSON.stringify(layer.getLatLngs()))
            }

            if(latlngLayers.includes(layer.options.type)) {
                layer.originalCoords = JSON.parse(JSON.stringify(layer.getLatLng()))
            }
            
            this.setState({ mode: 'view' })
        })

        layer.on('revert', () => {
            if(layer.editing) {
                layer.editing.disable()
            }

            if(latlngsLayers.includes(layer.options.type)) {
                layer.setLatLngs(layer.originalCoords)
                const newLayer = L[layer.options.type](layer.getLatLngs(), layer.options)
                this.setLayer(newLayer)
                newLayer._events = layer._events
                
                this.state.map.removeLayer(layer)
                this.state.map.addLayer(newLayer)
            }

            if(latlngLayers.includes(layer.options.type)) {
                layer.setLatLng(layer.originalCoords)
                const newLayer = L[layer.options.type](layer.getLatLng(), layer.options)
                if(layer.options.type == 'circle') {
                    newLayer.setRadius(layer.originalRadius)
                }
                this.setLayer(newLayer)
                this.state.map.removeLayer(layer)
                this.state.map.addLayer(newLayer)
            }

            this.setState({ mode: 'view' })
        })

        if(this.props.setLayer) {
            this.props.setLayer(layer)
        }
    }

    componentDidMount() {
        const mapID = this.props.id ? this.props.id : 'mapid'
        // initialize map
        var map = L.map(mapID, { 
            ...this.props.mapOptions, 
            zoomControl: false, 
            scrollWheelZoom: false, // disable original zoom function
            smoothWheelZoom: true,  // enable smooth zoom 
            smoothSensitivity: 2,   // zoom speed. default is 1
            zoomSnap: 0, 
            attributionControl: !this.props.disableAttributionControl}).setView(this.props.setView ? this.props.setView : [59.249234, 14.512052], this.props.setZoomView ? this.props.setZoomView : 5);

        map.on("draw:created", e => this.finishDraw(e))
        map.on("zoom", e => this.handleZoom(e))
        map.on("click", e => this.handleMapClick(e))

        // Add the default openstreetmap to the map
        let baseMap = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, tileSize: 512, zoomOffset: -1 })
        baseMap.name = 'Open Street Map'

        // Set a different map if send to props
        if(this.props.baseMap) {
            let baseMapObj = baseMaps.find(m => m.name == this.props.baseMap)
            if(baseMapObj) {
                if(baseMapObj?.type == "wms") {
                    baseMap = L.tileLayer.wms(baseMapObj.url, {...baseMapObj.config })
                } else {
                    baseMap = L.tileLayer(baseMapObj.url, baseMapObj.config)
                }
            }
            baseMap.name = baseMapObj.name
        }
       
        baseMap.addTo(map)

        // Setting styles for all the edit handlers in the map
        let polyHandler = polyIcon
        let moveHandler = moveIcon
        let resizeHandler = resizeIcon

        if(this.props.polyIcon) {
            polyHandler = this.props.polyIcon
        }

        if(this.props.moveIcon) {
            moveHandler = this.props.moveIcon
        }

        if(this.props.resizeIcon) {
            resizeHandler = this.props.resizeIcon
        }
        
        L.Edit.PolyVerticesEdit.prototype.options.touchIcon = polyHandler

        L.Edit.Circle.prototype.options.touchMoveIcon = moveHandler
        L.Edit.Circle.prototype.options.touchResizeIcon = resizeHandler

        L.Edit.CircleMarker.prototype.options.touchMoveIcon = moveHandler

        L.Edit.Rectangle.prototype.options.touchMoveIcon = moveHandler
        L.Edit.Rectangle.prototype.options.touchResizeIcon = resizeHandler

        this.setState({ map: map, baseMap: baseMap });
        ensureSwedishLeafletDrawLocale();
    }

    render() {
        return <>
            <div id={this.props.id ? this.props.id : 'mapid'} className={this.props.className} style={{ height: "100%", width: "100%", opacity: 1, ...this.props.style }}></div>
        </>
    }
}

export default VBLeaflet;